import { useState } from "react";
import { MapPin, Mail, FileBadge, Clock, Send, CheckCircle2, Loader2, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { useLanguage } from "@/context/LanguageContext";
import { Reveal, Eyebrow } from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
    const { t } = useLanguage();
    const c = t.contact;
    const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
    const [sending, setSending] = useState(false);
    const [sent, setSent] = useState(false);

    const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

    const onSubmit = async (e) => {
        e.preventDefault();
        setSending(true);
        try {
            const res = await fetch(`${API}/contact`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    name: form.name.trim(),
                    email: form.email.trim(),
                    phone: form.phone.trim() || null,
                    message: form.message.trim(),
                }),
            });
            if (!res.ok) throw new Error("send failed");
            setSent(true);
            toast.success(c.successTitle);
        } catch {
            toast.error(c.errorMsg);
        } finally {
            setSending(false);
        }
    };

    const infoItems = [
        { icon: MapPin, label: c.addressLabel, value: "236/13A Điện Biên Phủ, Phường Gia Định, TP. Hồ Chí Minh", testid: "contact-info-address" },
        { icon: Mail, label: c.emailLabel, value: "admin@maxthsolutions.org", href: "mailto:admin@maxthsolutions.org", testid: "contact-info-email" },
        { icon: FileBadge, label: c.taxLabel, value: "0319622735", testid: "contact-info-tax" },
        { icon: Clock, label: c.hoursLabel, value: c.hoursValue, testid: "contact-info-hours" },
    ];

    const inputCls =
        "w-full rounded-xl border border-slate-700/80 bg-ink px-4 py-3 text-sm text-slate-200 placeholder:text-slate-600 outline-none transition-colors focus:border-neon/60 focus:ring-1 focus:ring-neon/30";

    return (
        <div data-testid="contact-page" className="pt-36 sm:pt-44">
            <section className="relative mx-auto max-w-7xl px-5 sm:px-8">
                <div className="grid-bg absolute inset-0 -z-10" aria-hidden="true" />
                <Reveal>
                    <Eyebrow>{c.eyebrow}</Eyebrow>
                    <h1 className="max-w-3xl font-display text-4xl font-extrabold leading-[1.18] tracking-tight text-white sm:text-5xl lg:text-6xl">
                        {c.title}
                    </h1>
                    <p className="mt-6 max-w-2xl text-sm leading-relaxed text-slate-300 sm:text-base">{c.sub}</p>
                </Reveal>
            </section>

            <section className="mx-auto max-w-7xl px-5 py-20 sm:px-8 sm:py-24">
                <div className="grid gap-8 lg:grid-cols-5">
                    {/* INFO */}
                    <Reveal className="lg:col-span-2">
                        <div className="glass h-full rounded-2xl p-7 sm:p-8">
                            <h2 className="font-display text-xl font-bold text-white">{c.infoTitle}</h2>
                            <div className="mt-8 space-y-6">
                                {infoItems.map((item) => (
                                    <div key={item.label} className="flex items-start gap-4">
                                        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-slate-700 bg-ink text-neon">
                                            <item.icon className="h-4 w-4" />
                                        </div>
                                        <div>
                                            <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">
                                                {item.label}
                                            </p>
                                            {item.href ? (
                                                <a
                                                    href={item.href}
                                                    data-testid={item.testid}
                                                    className="mt-1 block text-sm font-medium text-slate-200 transition-colors hover:text-neon"
                                                >
                                                    {item.value}
                                                </a>
                                            ) : (
                                                <p data-testid={item.testid} className="mt-1 text-sm font-medium text-slate-200">
                                                    {item.value}
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                            <div className="grid-bg mt-9 overflow-hidden rounded-xl border border-slate-800 bg-ink2 p-5">
                                <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">
                                    MAXTH Solutions HQ
                                </p>
                                <p className="mt-2 font-display text-sm font-bold text-slate-300">
                                    Gia Định — TP. Hồ Chí Minh, Việt Nam
                                </p>
                            </div>
                        </div>
                    </Reveal>

                    {/* FORM */}
                    <Reveal delay={0.1} className="lg:col-span-3">
                        <div className="glass h-full rounded-2xl p-7 sm:p-9">
                            {sent ? (
                                <div className="flex h-full flex-col items-center justify-center py-12 text-center" data-testid="contact-success">
                                    <CheckCircle2 className="h-14 w-14 text-mint" />
                                    <h2 className="mt-6 font-display text-2xl font-bold text-white">
                                        {c.successTitle}
                                    </h2>
                                    <p className="mt-3 max-w-sm text-sm leading-relaxed text-slate-400">
                                        {c.successText}
                                    </p>
                                    <button
                                        type="button"
                                        data-testid="contact-send-another-button"
                                        onClick={() => {
                                            setSent(false);
                                            setForm({ name: "", email: "", phone: "", message: "" });
                                        }}
                                        className="mt-8 rounded-full border border-slate-700 px-6 py-3 text-sm font-medium text-slate-200 transition-colors hover:border-neon/60 hover:text-neon"
                                    >
                                        {c.sendAnother}
                                    </button>
                                </div>
                            ) : (
                                <form onSubmit={onSubmit} data-testid="contact-form">
                                    <h2 className="font-display text-xl font-bold text-white">{c.formTitle}</h2>
                                    <div className="mt-7 grid gap-5 sm:grid-cols-2">
                                        <div>
                                            <label htmlFor="contact-name" className="mb-2 block text-xs font-medium text-slate-400">
                                                {c.nameLabel} <span className="text-neon">*</span>
                                            </label>
                                            <input
                                                id="contact-name"
                                                name="name"
                                                required
                                                minLength={2}
                                                maxLength={120}
                                                value={form.name}
                                                onChange={onChange}
                                                placeholder={c.namePlaceholder}
                                                data-testid="contact-name-input"
                                                className={inputCls}
                                            />
                                        </div>
                                        <div>
                                            <label htmlFor="contact-email" className="mb-2 block text-xs font-medium text-slate-400">
                                                Email <span className="text-neon">*</span>
                                            </label>
                                            <input
                                                id="contact-email"
                                                name="email"
                                                type="email"
                                                required
                                                value={form.email}
                                                onChange={onChange}
                                                placeholder={c.emailPlaceholder}
                                                data-testid="contact-email-input"
                                                className={inputCls}
                                            />
                                        </div>
                                    </div>
                                    <div className="mt-5">
                                        <label htmlFor="contact-phone" className="mb-2 block text-xs font-medium text-slate-400">
                                            {c.phoneLabel}
                                        </label>
                                        <input
                                            id="contact-phone"
                                            name="phone"
                                            value={form.phone}
                                            onChange={onChange}
                                            placeholder={c.phonePlaceholder}
                                            data-testid="contact-phone-input"
                                            className={inputCls}
                                        />
                                    </div>
                                    <div className="mt-5">
                                        <label htmlFor="contact-message" className="mb-2 block text-xs font-medium text-slate-400">
                                            {c.messageLabel} <span className="text-neon">*</span>
                                        </label>
                                        <textarea
                                            id="contact-message"
                                            name="message"
                                            required
                                            minLength={10}
                                            maxLength={4000}
                                            rows={6}
                                            value={form.message}
                                            onChange={onChange}
                                            placeholder={c.messagePlaceholder}
                                            data-testid="contact-message-input"
                                            className={`${inputCls} resize-y`}
                                        />
                                    </div>
                                    <button
                                        type="submit"
                                        disabled={sending}
                                        data-testid="contact-submit-button"
                                        className="group mt-8 inline-flex items-center gap-2 rounded-full bg-neon px-8 py-4 text-sm font-semibold text-ink transition-all hover:bg-white disabled:cursor-not-allowed disabled:opacity-60"
                                    >
                                        {sending ? (
                                            <>
                                                <Loader2 className="h-4 w-4 animate-spin" />
                                                {c.sending}
                                            </>
                                        ) : (
                                            <>
                                                {c.submit}
                                                <Send className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-0.5" />
                                            </>
                                        )}
                                    </button>
                                </form>
                            )}
                        </div>
                    </Reveal>
                </div>
            </section>

            {/* MAP */}
            <section className="mx-auto max-w-7xl px-5 pb-28 sm:px-8" data-testid="contact-map-section">
                <Reveal>
                    <div className="flex flex-wrap items-end justify-between gap-5">
                        <div>
                            <Eyebrow>{c.mapEyebrow}</Eyebrow>
                            <h2 className="font-display text-2xl font-bold tracking-tight text-white sm:text-3xl">
                                {c.mapTitle}
                            </h2>
                            <p className="mt-3 text-sm text-slate-300">
                                236/13A Điện Biên Phủ, Phường Gia Định, TP. Hồ Chí Minh
                            </p>
                        </div>
                        <a
                            href="https://www.google.com/maps/search/?api=1&query=236%2F13A+%C4%90i%E1%BB%87n+Bi%C3%AAn+Ph%E1%BB%A7%2C+Ph%C6%B0%E1%BB%9Dng+Gia+%C4%90%E1%BB%8Bnh%2C+TP.+H%E1%BB%93+Ch%C3%AD+Minh"
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="contact-map-directions-link"
                            className="group inline-flex items-center gap-2 rounded-full border border-slate-700 px-5 py-2.5 text-sm text-slate-200 transition-colors hover:border-neon/60 hover:text-neon"
                        >
                            {c.mapCta}
                            <ExternalLink className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                        </a>
                    </div>
                    <div className="group relative mt-8 overflow-hidden rounded-2xl border border-slate-800">
                        <iframe
                            title="MAXTH Solutions — 236/13A Điện Biên Phủ"
                            data-testid="contact-map-iframe"
                            src="https://www.google.com/maps?q=236%2F13A+%C4%90i%E1%BB%87n+Bi%C3%AAn+Ph%E1%BB%A7%2C+Ph%C6%B0%E1%BB%9Dng+Gia+%C4%90%E1%BB%8Bnh%2C+TP.+H%E1%BB%93+Ch%C3%AD+Minh&output=embed"
                            width="100%"
                            height="440"
                            loading="lazy"
                            referrerPolicy="no-referrer-when-downgrade"
                            allowFullScreen
                            className="block w-full"
                            style={{ border: 0, filter: "invert(0.92) hue-rotate(180deg) contrast(0.92) saturate(0.85)" }}
                        />
                    </div>
                </Reveal>
            </section>
        </div>
    );
}
